// ═══════════════════════════════════════════════════════════
// LANDING PAGE CONTENT MODEL
// All marketing copy, section data, and CTA variants for
// dpdp.codexero.ai — edit here to update the entire page.
// ═══════════════════════════════════════════════════════════

import {
  Shield, FileCheck, AlertTriangle, Eye, EyeOff, Lock,
  Building2, BarChart3, ClipboardCheck, MapPin, Users, Factory,
  Landmark, GraduationCap, Stethoscope, ShoppingCart, Wifi, Cpu,
  Target, FileWarning, Scale, ShieldCheck, Zap, Truck, Briefcase
} from 'lucide-react';

export const LOGO_URL = "https://customer-assets.emergentagent.com/job_readiness-check-27/artifacts/el8npnpt_CXOlogo.png";

// ──────────────────────────────────────────────────────────
// BRAND
// ──────────────────────────────────────────────────────────

export const BRAND = {
  name: 'CX0',
  company: 'CodeXero',
  domain: 'codexero.ai',
  supportEmail: 'support@codexero.ai',
  website: 'https://www.codexero.ai',
  colors: {
    navy: '#003366',
    teal: '#008080',
    tealDark: '#006666',
    darkNavy: '#002244',
  },
};

// ──────────────────────────────────────────────────────────
// HEADER & NAV
// ──────────────────────────────────────────────────────────

export const HEADER = {
  navLinks: [
    { label: 'PII Finder', action: 'scroll-pii', testId: 'nav-pii-finder' },
    { label: 'Login', href: '/login', testId: 'header-login-link' },
  ],
  ctaDesktop: {
    label: 'Start Executive Readiness Assessment',
    href: '/request-access',
    testId: 'header-get-access-btn',
  },
  ctaMobile: {
    label: 'Start Assessment',
    href: '/request-access',
    testId: 'header-cta-mobile',
  },
};

// ──────────────────────────────────────────────────────────
// HERO SECTION
// ──────────────────────────────────────────────────────────

export const HERO = {
  badge: {
    icon: Shield,
    label: 'Aligned to DPDP Rules 2025',
  },
  headline: 'Do You Know What Personal Data Your Business Handles?',
  subheadline: "Most Indian enterprises can't answer this question. CX0 helps you find out — and get DPDP-ready before the penalties hit.",
  primaryCta: {
    label: 'Try Free PII Finder',
    action: 'scroll-pii',
    testId: 'hero-cta-btn',
  },
  secondaryCta: {
    label: 'Start Executive Readiness Assessment',
    href: '/request-access',
    testId: 'hero-secondary-cta',
  },
  statsCards: [
    { value: '15', label: 'Compliance Pillars', color: 'white' },
    { value: '83', label: 'Assessment Questions', color: 'white' },
    { value: '250 Cr', label: 'Max Penalty', color: '#FF6B6B' },
    { value: 'Self-Paced', label: 'Complete at Your Speed', color: '#4ADE80' },
  ],
  progressBars: [
    { label: 'Data Inventory', pct: 65 },
    { label: 'Consent Mgmt', pct: 40 },
    { label: 'Security Safeguards', pct: 75 },
    { label: 'Breach Response', pct: 30 },
  ],
};

// ──────────────────────────────────────────────────────────
// TRUST STRIP
// ──────────────────────────────────────────────────────────

export const TRUST_BADGES = [
  { icon: Shield, label: 'DPDP Act 2023', testId: 'trust-badge-1' },
  { icon: FileCheck, label: 'Rules 2025 Aligned', testId: 'trust-badge-2' },
  { icon: Lock, label: 'MeitY Framework', testId: 'trust-badge-3' },
  { icon: null, label: 'Powered by CodeXero', testId: 'trust-badge-4', useLogo: true },
];

// ──────────────────────────────────────────────────────────
// PROBLEM SECTION
// ──────────────────────────────────────────────────────────

export const PROBLEM_SECTION = {
  badge: 'The Problem',
  headline: 'Your Organisation Handles More Personal Data Than You Think',
  cards: [
    {
      icon: EyeOff,
      title: 'Invisible Data Trails',
      description: 'Most organisations process personal data across 5+ departments without a single unified view of what they collect or why.',
    },
    {
      icon: AlertTriangle,
      title: 'Penalties Up to 250 Crore',
      description: 'DPDP Rules 2025 are in force. Non-compliance carries penalties up to 250 Crore per instance. The clock is ticking.',
    },
    {
      icon: Eye,
      title: 'Blind Spots Are the Biggest Risk',
      description: "Cookies, biometric attendance, vendor data sharing — the data you don't know about is the data that creates liability.",
    },
  ],
};

// ──────────────────────────────────────────────────────────
// VALUE PROPOSITION SECTION
// ──────────────────────────────────────────────────────────

export const VALUE_SECTION = {
  badge: 'The CX0 Solution',
  headline: 'From Confusion to Compliance — In a Structured, Measurable Way',
  description: "CX0's DPDP Readiness Assessment gives your organisation a clear, scored, and actionable compliance posture. No guesswork. No generic checklists.",
  cards: [
    { icon: Target, title: 'Map Your Data Landscape', desc: 'Identify every category of personal data your organisation handles across all departments and processes.' },
    { icon: BarChart3, title: 'Score Your Readiness', desc: '15-pillar scoring engine gives you a clear, quantified compliance posture with pillar-wise breakdowns.' },
    { icon: FileWarning, title: 'Spot Your Gaps', desc: 'Full gap register mapped to DPDP Act sections and Rules 2025. Know exactly where you stand.' },
    { icon: MapPin, title: 'Build Your Roadmap', desc: '12-18 month phased compliance roadmap with owner mapping, effort estimates, and priority sequencing.' },
  ],
};

// ──────────────────────────────────────────────────────────
// INDUSTRY BLIND SPOTS (landing page section)
// ──────────────────────────────────────────────────────────

export const INDUSTRY_BLINDSPOTS_SECTION = {
  badge: 'Industry Intelligence',
  headline: 'Every Industry Has Data Blind Spots',
  description: 'Personal data hides in industry-specific workflows. Find out what your sector commonly misses.',
  cards: [
    { id: 'bfsi', icon: Landmark, label: 'BFSI', stat: '15+ data points per customer', description: 'KYC, loan files, and insurance claims create the densest personal data per individual. Call recordings and CIBIL pulls are commonly missed.' },
    { id: 'healthcare', icon: Stethoscope, label: 'Healthcare', stat: 'Most sensitive data category', description: 'Patient records, prescriptions, and lab reports shared via WhatsApp and email are regulated processing events under DPDP.' },
    { id: 'it_saas', icon: Cpu, label: 'IT / SaaS', stat: '10+ tools processing user data', description: 'Product analytics, error logs, and CRM data make SaaS firms both data fiduciaries and processors with dual obligations.' },
    { id: 'retail', icon: ShoppingCart, label: 'Retail & E-commerce', stat: '3+ third parties per order', description: 'Every order shares customer data with payment gateways, logistics partners, and marketing platforms — all DPDP-regulated.' },
    { id: 'education', icon: GraduationCap, label: 'Education', stat: 'Strictest DPDP category', description: "Children's data requires verifiable parental consent. Biometric attendance and learning analytics of minors carry the highest risk." },
    { id: 'manufacturing', icon: Factory, label: 'Manufacturing', stat: 'Highest-risk biometric data', description: "Fingerprint attendance, CCTV footage, and contractor databases are high-risk data stores that most factories haven't mapped." },
    { id: 'logistics', icon: Truck, label: 'Logistics', stat: 'Continuous location processing', description: 'GPS tracking, delivery OTPs, and customer phone exposure happen on every shipment — each is a DPDP processing event.' },
    { id: 'real_estate', icon: MapPin, label: 'Real Estate', stat: '#1 uncontrolled data channel', description: 'WhatsApp is the de facto CRM in Indian real estate. Buyer leads and KYC documents flow through it daily without controls.' },
    { id: 'professional_services', icon: Briefcase, label: 'Professional Services', stat: 'Dual DPDP obligations', description: "Consulting and staffing firms handle their own data plus clients' personal data — most haven't mapped where one obligation ends." },
  ],
};

// ──────────────────────────────────────────────────────────
// PII FINDER SECTION (wrapper on landing page)
// ──────────────────────────────────────────────────────────

export const PII_FINDER_SECTION = {
  badge: 'Free Discovery Tool',
  headline: 'PII Finder & DPDP Impact Check',
  description: '6 quick questions about your business. Discover what personal data you handle, where the blind spots are, and how DPDP affects you — in under 5 minutes.',
};

// ──────────────────────────────────────────────────────────
// REPORT FEATURES (what the paid product delivers)
// ──────────────────────────────────────────────────────────

export const REPORT_SECTION = {
  badge: 'Full Assessment Output',
  headline: 'Executive-Grade DPDP Readiness, Delivered in One Report',
  description: 'Receive an AI-enriched compliance report with pillar-wise scoring, risk exposure, evidence readiness, and an 18-month remediation roadmap tailored for decision-makers.',
  features: [
    { icon: BarChart3, label: 'Pillar-wise Compliance Score' },
    { icon: FileCheck, label: 'Full Gap Register vs DPDP' },
    { icon: ClipboardCheck, label: 'Evidence Readiness Planner' },
    { icon: Shield, label: 'SDF Risk Assessment' },
    { icon: MapPin, label: '18-Month Compliance Roadmap' },
    { icon: Scale, label: 'Penalty Exposure Indicator' },
  ],
  steps: [
    { step: '01', title: 'Request Access', desc: 'Submit your details below. Your account will be activated within 24 hours of payment confirmation — and our team is available to assist at any step.' },
    { step: '02', title: 'Complete Assessment', desc: 'Answer 83 structured questions across 15 DPDP compliance pillars.' },
    { step: '03', title: 'Receive Your Report', desc: 'Get your comprehensive, AI-enriched compliance report from CodeXero.' },
  ],
};

// ──────────────────────────────────────────────────────────
// ASSESSMENT FRAMEWORK (pillars & industry icons)
// ──────────────────────────────────────────────────────────

export const FRAMEWORK = {
  badge: 'Assessment Framework',
  headline: '15 Pillars of DPDP Readiness',
  pillars: [
    'Applicability & Data Inventory',
    'Consent Management & Notice',
    'Data Principal Rights',
    'Data Security & Safeguards',
    'Breach Response & Notification',
    'Governance, Accountability & Training',
    'Third-Party & Cross-Border Management',
    "Children's & Special Category Data",
    'Significant Data Fiduciary (SDF) Risk',
    'Evidence Readiness',
    'Readiness Roadmap',
    'Data Retention & Erasure',
    'Data Protection Impact Assessment (DPIA)',
    'Grievance Redressal & DPB Escalation',
    'Privacy by Design',
  ],
  industryIcons: [
    { icon: Landmark, label: 'BFSI' },
    { icon: Stethoscope, label: 'Healthcare' },
    { icon: Cpu, label: 'IT / SaaS' },
    { icon: Factory, label: 'Manufacturing' },
    { icon: ShoppingCart, label: 'Retail' },
    { icon: GraduationCap, label: 'Education' },
    { icon: Wifi, label: 'Telecom' },
    { icon: Building2, label: 'Government' },
  ],
  industryStripLabel: 'Built for Indian Enterprises Across',
};

// ──────────────────────────────────────────────────────────
// FINAL CTA SECTION
// ──────────────────────────────────────────────────────────

export const FINAL_CTA = {
  icon: Zap,
  headline: 'Ready to Know Where You Stand on DPDP?',
  description: "Get your organisation's compliance score, gap analysis, and roadmap. The CX0 Executive Readiness Assessment gives you a fast, decision-ready view of where you stand.",
  primaryCta: {
    label: 'Start Executive Readiness Assessment',
    href: '/request-access',
    testId: 'bottom-cta-btn',
  },
  secondaryCta: {
    label: 'Try PII Finder First',
    action: 'scroll-pii',
    testId: 'bottom-pii-cta',
  },
};

// ──────────────────────────────────────────────────────────
// FOOTER
// ──────────────────────────────────────────────────────────

export const FOOTER = {
  links: [
    { label: 'Privacy Policy', href: '#' },
  ],
};
